import { isKeywordGraphApp } from '../../appVariant'

const stepperFlows = {
    apps: {
        name: 'Learn to Use KeywordGraph',
        steps: [
            {
                label: '1. Get an overview of any topic',
                description: `Visualize search results for a certain topic to understand an existing discourse. Get an overview of the main ideas and see what's missing. Bridge the content gaps with your own ideas or use AI.`,
                menuHint: `Study a Market → Google Search Results`,
                showEvent: {
                    show: `openGoogleSearchResultsDialog`,
                    closeOnShow: false,
                },
                learnMore:
                    'https://support.noduslabs.com/hc/en-us/articles/360013525580-Getting-an-Overview-of-a-Topic-or-Discourse-using-the-Google-Search-Results-App',
            },
            {
                label: '2. Discover what people search for',
                description: `Visualize what else people search for in relation to the topic you chose. Find recurrent patterns in search queries and target them to increase the reach of your content.`,
                menuHint: `Brainstorming → What People Search For`,
                showEvent: {
                    show: `openKeywordResearchDialog`,
                    closeOnShow: false,
                },
                learnMore:
                    'https://support.noduslabs.com/hc/en-us/articles/19454930569628-Keyword-Research-Workflow-Study-Demand-Understand-Search-Intent',
            },
            {
                label: `3. Get an overview and find content gaps in a document`,
                description: `Paste your notes, text, a URL, or any PDF document and reveal the content gaps in the discourse to see what's missing. Use the built-in AI to generate questions to bridge those gaps and generate new ideas.`,
                menuHint: `Analyze Texts → Add a Text`,
                showEvent: {
                    show: `openTextAnalysisDialog`,
                    closeOnShow: false,
                },
                learnMore:
                    'https://support.noduslabs.com/hc/en-us/articles/8113730778012-Text-Analysis-Workflow-Get-an-Topical-Overview-and-Find-Content-Gaps',
            },
            {
                label: `4. Explore how AI sees a topic`,
                description: `Build a knowledge graph using AI. Then select the concepts and topics you want to explore and generate ideas based on them using the built-in AI.`,
                menuHint: `Brainstorm → AI Generated → Knowledge Graph`,
                showEvent: {
                    show: `openKnowledgeGraphDialog`,
                    closeOnShow: false,
                },
                learnMore:
                    'https://support.noduslabs.com/hc/en-us/articles/5765029210002-Brainstorming-Workflow-Using-Knowledge-Graphs-and-AI-to-Explore-a-Topic',
            },
        ],
    },
    basicOld: {
        name: 'Basic Exploratory Workflow',
        steps: [
            {
                label: 'Add or Import a Text',
                description: `You can copy and paste your text, upload a file, or import Google search results or GPT AI ideas. After you add the text it will be represented as a network graph, so you can see the relations within.`,
            },
            {
                label: 'Explore Text Network Visualization',
                description: `Explore a discourse like a social network: the words are the people and their relations are the connections. You will get an overview of the most influential ideas and their relations.`,
            },
            {
                label: 'Identify the Top Concepts',
                description: `Locate the bigger nodes on the graph. These are the most influential concepts in this text.`,
                menuHint: `Graph Panel → Concepts`,
                showEvent: {
                    show: `selectTopNodes`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Inspect the Topics & High-Level Ideas',
                description: `The words that tend to appear in the same context belong to the same topic and have the same color on the graph. Use GPT AI to generate the names for these topics.`,
                menuHint: `Analytics Panel → Topics → AI: Reveal High-Level Ideas`,
                showEvent: {
                    show: `showAICategories`,
                    undo: `hideAICategories`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Reveal the Structural Gaps',
                description: `Some of the topics inside the text may not be well connected and produce structural gaps within. These gaps can be used to generate insight because they connect the ideas in new ways that are contextually relevant.`,
                menuHint: `Analytics panel → Gap Insight`,
                showEvent: {
                    show: `highlightStructuralGap`,
                    undo: `unHighlightStructuralGap`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Bridge the Structural Gaps with GPT AI',
                description: `Think of a research question or an idea that can bridge the structural gap. Use GPT AI for inspiration, if needed. Click "Show Another Gap" to load more structural gaps. Bridge them with your own ideas or using AI.`,
                menuHint: `Analytics panel → Gap Insight → AI: Research Question`,
                showEvent: {
                    show: `bridgeStructuralGap`,
                    undo: `unbridgeStructuralGap`,
                },
            },
            {
                label: 'Question | Research | Ideate with AI',
                description: `Use the built-in AI not to think for you, but to think with you: generate ideas, assertions, challenges, and questions.`,
                menuHint: `AI Insight panel → Question / Respond / Challenge | AI: Summary`,
                showEvent: {
                    show: `showAIPanel`,
                    undo: `hideAIPanel`,
                },
            },
            {
                label: 'Reveal Underlying Ideas to Discover Nuance',
                description: `Remove the most influential concepts from the graph to reveal the underlying ideas around them. This will help you extract the nuance and explore the peripheral topics that make a discourse special and can potentially serve as low-barrier entrance points.`,
                menuHint: `Graph → Select Nodes → click "Hide"`,
                showEvent: {
                    show: `revealUnderlyingIdeas`,
                    undo: `undoUnderlyingIdeas`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Reiterate Steps 2 → 8',
                description: `Repeat steps 2 → 8 to get an overview and discover the gaps between ideas on deeper levels. Write down some insights you have while exploring the peripheral nodes and underlying ideas to make your research special and specific.`,
            },
            {
                label: 'Save and Share Your Insights',
                description: `You can write down your ideas into project notes. You can also export the graph as an interactive image or as a high-resolution image.`,
                menuHint: `Project Notes Insights → Copy / Export `,
                showEvent: {
                    show: `showProjectNotesPanelAndShare`,
                    undo: `hideProjectNotesPanelAndShare`,
                },
            },
        ],
    },
    basic: {
        name: 'Text Exploration Workflow',
        steps: [
            {
                label: '1. Add or Import a Text',
                description: `You can copy and paste your text, upload a file, import AI-generated ideas, or Google search results. After you add the text, the concepts are represented as a network graph. The words are the nodes, and the co-occurrences are the connections.`,
            },
            {
                label: '2. Get a Visual Summary of the Main Topics',
                description: `Reveal the main topical clusters in the text as well as AI-generated summary and insights to get a better understanding of the content.`,
                menuHint: `Graph Panel → Topics → AI: Graph Summary`,
                showEvent: {
                    show: `showAICategoriesAndAi`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '3. Reveal the Blind Spots',
                description: `Some of the topics inside the text may not be well connected and produce structural gaps within. These gaps can be used to generate insight because they connect the ideas in new ways that are contextually relevant. Use the AI to generate a question to bridge this gap with a new idea.`,
                menuHint: `Graph Panel → Gaps`,
                showEvent: {
                    show: `highlightStructuralGapAndBridge`,
                    undo: `unHighlightStructuralGap`,
                    closeOnShow: true,
                },
            },
            {
                label: '4. Explore the Top Concepts & Topics',
                description: `Locate the bigger nodes or topics (same color node clusters) on the graph. These are the most influential concepts / topics in this text. Click those to see the context where they appear and related ideas.`,
                menuHint: `Graph Panel → Concepts`,
                showEvent: {
                    show: `selectTopNodes`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '5. Save and Share Your Insights',
                description: `You can write down your ideas into project notes. You can also export the graph as an interactive image or as a high-resolution image.`,
                menuHint: `Project Notes Insights → Copy / Export `,
                showEvent: {
                    show: `showProjectNotesPanelAndShare`,
                    undo: `hideProjectNotesPanelAndShare`,
                },
            },
        ],
    },
    diagnostics: {
        name: 'Text Diagnostics Workflow',
        steps: [
            {
                label: 'Add or Import a Text',
                description: `You can copy and paste your text, upload, or import Google search results or GPT AI ideas. After you add the text it will be visualized as an interactive network graph, so you can discover the main patterns and relations.`,
            },
            {
                label: 'Explore Text Network Visualization',
                description: `Reveal the most influential concepts (bigger on the graph), topical clusters (have the same color and location), explore the relations and gaps between the concepts and topics. If you see some words that shouldn't be there, select and hide them from the graph`,
            },
            {
                label: 'Reveal High-Level Ideas',
                description: `Think of high-level ideas that unify the concepts in topical clusters shown on the graph with the same color. Use GPT AI to generate them automatically.`,
                menuHint: `Analytics Panel → click AI: Reveal High-Level Ideas`,
                showEvent: {
                    show: `showAICategories`,
                    undo: `hideAICategories`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Identify Relevant & Top Concepts',
                description: `Locate the concepts that are relevant for you personally on the graph or find the top influential nodes. Select them to study the context where they appear.`,
                menuHint: `Graph Panel → Concepts`,
                showEvent: {
                    show: `selectTopNodes`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Explore Top Relations',
                description: `Locate the most prominent relations between the concepts in text, helping you understand the nuance and context better.`,
                menuHint: `Analytics → Relations`,
                showEvent: {
                    show: `selectTopRelations`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Explore the Main Topical Clusters',
                description: `The main topics are formed by the topical clusters in text: the concepts that tend to often co-occur together in recurring patterns.`,
                menuHint: `Analytics Panel → Topics → Topical Clusters`,
                showEvent: {
                    show: `selectTopCommunities`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Write Down Your Insights',
                description: `Reformulating ideas in your own words helps you to understand and retain them better. Use the Your Insights: Project Notes panel to write down the insights you have so far.`,
                menuHint: `Project Notes → write → click Save`,
                showEvent: {
                    show: `showProjectNotesPanel`,
                    undo: `hideProjectNotesPanel`,
                },
            },
            {
                label: 'Zoom In on Each Topic',
                description: `Select some topics in the Analytics panel to see what groups of concepts belong to them and to understand each one of them a little better.`,
                menuHint: `Analytics Panel → Topics → click a Cluster`,
                showEvent: {
                    show: `selectTopCommunity`,
                    undo: `undoSelectTopCommunity`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Locate Discourse Connector Points',
                description: `Some concepts have high influence even if they don't appear too often. These are the easiest entry points into this discourse and can be used to develop it further or to connect it to another discourse.`,
                menuHint: `Analytics → Gap Insights → Discourse Connectors`,
                showEvent: {
                    show: `highlightLatentNodesOnGraph`,
                    undo: `unHighlightNodesOnGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Reveal the Structural Gaps',
                description: `Some of the topical clusters or concepts inside the text are not well connected and produce structural gaps in the network. These gaps can be used to generate insight because they connect the ideas in new ways that are contextually relevant. Use the graph and the Gap Insight tab in the Analytics panel to find the structural gaps in the discourse.`,
                menuHint: `Analytics panel → Gap Insight`,
                showEvent: {
                    show: `highlightStructuralGap`,
                    undo: `unHighlightStructuralGap`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Bridge the Structural Gaps',
                description: `Think of a research question or an idea that can bridge the structural gap. Use GPT AI for inspiration, if needed. Click "Show Another Gap" to load more structural gaps. Bridge them with your own ideas or using AI.`,
                menuHint: `Analytics panel → Gap Insight → AI: Research Question`,
                showEvent: {
                    show: `bridgeStructuralGap`,
                    undo: `unbridgeStructuralGap`,
                },
            },
            {
                label: 'Save Your Research Questions & Insights',
                description: `Reformulating insights helps to define and retain them better. Save some interesting questions and ideas that you or GPT AI came up with so far.`,
                menuHint: `Project Notes → write → save`,
                showEvent: {
                    show: `showProjectNotesPanel`,
                    undo: `hideProjectNotesPanel`,
                },
            },
            {
                label: 'Question | Challenge | Ideate with AI',
                description: `Use the built-in AI not to think for you, but to think with you: generate ideas, assertions, challenges, and questions. Edit and save what you like to your Project Notes. Run a few iterations until you exhaust possibilities. Use Analytics > AI: Summary buttons to generate outlines for the text or selected topics.`,
                menuHint: `AI Insight panel → Question / Respond / Challenge | AI: Summary`,
                showEvent: {
                    show: `showAIPanel`,
                    undo: `hideAIPanel`,
                },
            },
            {
                label: 'Reveal Underlying Ideas to Discover Nuance',
                description: `Remove the most influential concepts from the graph to reveal the underlying ideas around them. This will help you extract the nuance and explore the peripheral topics that make a discourse special and can potentially serve as low-barrier entrance points.`,
                menuHint: `Graph → Select Nodes → click "Hide"`,
                showEvent: {
                    show: `revealUnderlyingIdeas`,
                    undo: `undoUnderlyingIdeas`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Reiterate Steps 2 → 14',
                description: `Repeat steps 2 → 14 to get an overview and discover the gaps between ideas on deeper levels. Write down some insights you have while exploring the peripheral nodes and underlying ideas to make your research special and specific.`,
            },
            {
                label: 'Reset the Graph',
                description: `Once the possibilities are exhausted, return all the hidden nodes in the graph and reset the graph, to bring it to the original shape.`,
                menuHint: `right Graph Menu → undo Hide | Reset All`,
                showEvent: {
                    show: `hardResetGraph`,
                    undo: `unhardResetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Import More Data',
                description: `Once the possibilities are exhausted, you can add more content in the original discourse. E.g. another, more specialized Google or AI query, a PDF file, your other texts. `,
                menuHint: `+ add | import panel  → add or import`,
                showEvent: {
                    show: `openEditorPanel`,
                    undo: `hideEditorPanel`,
                },
            },
            {
                label: 'Generate AI Content',
                description: `Use the AI Insight panel editor to query GPT and generate more releveant ideas and questions.`,
                menuHint: `AI Insight panel → Add AI Query`,
                showEvent: {
                    show: `showAIPanelEditor`,
                    undo: `hideAIPanel`,
                },
            },
            {
                label: 'Finalize Your Project Notes Insights',
                description: `Read through your project notes insights and summarize them to get a better understanding of your research. You can use the AI: Summary and AI: Outline buttons to have AI do this for you.`,
                menuHint: `Project Notes Insights → AI: Summarize `,
                showEvent: {
                    show: `showProjectNotesPanel`,
                    undo: `hideProjectNotesPanel`,
                },
            },
            {
                label: 'Share Your Insights',
                description: `Copy your insights / project notes article outline and write an article based on those ideas. Paste this data to build a new graph to get a visual overview. You can also export the graph as an interactive image or as a high-resolution image.`,
                menuHint: `Project Notes Insights → Copy / Export `,
                showEvent: {
                    show: `showProjectNotesPanelAndShare`,
                    undo: `hideProjectNotesPanelAndShare`,
                },
            },
        ],
    },
    keywordResearchAdvanced: {
        name: 'Keyword Research Workflow',
        steps: [
            {
                label: 'Import Related Search Queries',
                description: `Get a list of suggested search queries for a certain topic to learn more about demand and understand what people are searching for.`,
            },
            {
                label: 'Study the Context around the Search Query',
                description: `Network visualization reveals the combinations of keywords used in the same search queries spatially organized in clusters of the same color. Most influential keywords are bigger.`,
            },
            {
                label: 'Reveal High-Level Topics',
                description: `Think of high-level categories that unify the concepts in keyword clusters shown on the graph with the same color. Use GPT AI to generate them automatically.`,
                menuHint: `Analytics Panel → click AI: Reveal High-Level Ideas`,
                showEvent: {
                    show: `showAICategories`,
                    undo: `hideAICategories`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Identify Relevant and Top Keywords',
                description: `Locate the concepts that are relevant for you personally on the graph or find the top influential nodes. Select them to see the search queries where they appear.`,
                menuHint: `Graph Panel → Concepts`,
                showEvent: {
                    show: `selectTopNodes`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Explore Top Combinations',
                description: `Locate top keyword combinations, click them to see which search phrases use these concepts.`,
                menuHint: `Analytics → Relations`,
                showEvent: {
                    show: `selectTopRelations`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Import More Keyword Suggestions',
                description: `Use the top combinations you identified and add more suggested keywords from Google or Adwords. To do that, click the Add / Import button.`,
                menuHint: `Analytics Panel → Topics → Topical Clusters`,
                showEvent: {
                    show: `openEditorPanel`,
                    undo: `hideEditorPanel`,
                },
            },
            {
                label: 'Write Down Your Keyword Ideas',
                description: `Save the top keywords and keyword combinations you found for your content / product strategy in the Project Notes. You can use it later to generate content and ideas with the help of AI.`,
                menuHint: `Project Notes → write → click Save`,
                showEvent: {
                    show: `showProjectNotesPanel`,
                    undo: `hideProjectNotesPanel`,
                },
            },
            {
                label: 'Zoom In on Topical Clusters',
                description: `Select some topics in the Analytics panel to see what groups of keywords belong to them and to understand each one of them a little better.`,
                menuHint: `Analytics Panel → Topics → click a Cluster`,
                showEvent: {
                    show: `selectTopCommunity`,
                    undo: `undoSelectTopCommunity`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Locate Discourse Connector Points',
                description: `Some keywords have high influence even if they don't appear too often. These are the easiest entry points into this discourse and can be used to develop it further or to connect it to another discourse.`,
                menuHint: `Analytics → Gap Insights → Discourse Connectors`,
                showEvent: {
                    show: `highlightLatentNodesOnGraph`,
                    undo: `unHighlightNodesOnGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Reveal the Structural Gaps',
                description: `Some topical clusters of keywords are not well connected and produce structural gaps in the network. These gaps can be used to generate insight because they connect the keywords (that represent demand) in new ways that are still contextually relevant. Use the graph and the Missing Content tab in the Analytics panel to find the structural gaps in the discourse.`,
                menuHint: `Analytics panel → Missing Content`,
                showEvent: {
                    show: `highlightStructuralGap`,
                    undo: `unHighlightStructuralGap`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Bridge the Structural Gaps',
                description: `Think of an innovative idea or content that can bridge the structural gap. Use GPT AI for inspiration, if needed. Click "Show Another Gap" to load more structural gaps. Bridge them with your own ideas or using AI.`,
                menuHint: `Analytics panel → Gap Insight → AI: Research Question`,
                showEvent: {
                    show: `bridgeStructuralGap`,
                    undo: `unbridgeStructuralGap`,
                },
            },
            {
                label: 'Save Your Research Questions & Insights',
                description: `Reformulating insights helps to define and retain them better. Save some interesting questions and ideas that you or GPT AI came up with so far.`,
                menuHint: `Project Notes → write → save`,
                showEvent: {
                    show: `showProjectNotesPanel`,
                    undo: `hideProjectNotesPanel`,
                },
            },
            {
                label: 'Question | Challenge | Ideate with AI',
                description: `Use the built-in AI not to think for you, but to think with you: generate ideas, assertions, challenges, and questions. Edit and save what you like to your Project Notes. Run a few iterations until you exhaust possibilities. Use Analytics > AI: Summary buttons to generate outlines for the text or selected topics.`,
                menuHint: `AI Insight panel → Question / Respond / Challenge | AI: Summary`,
                showEvent: {
                    show: `showAIPanel`,
                    undo: `hideAIPanel`,
                },
            },
            {
                label: 'Reveal Underlying Keywords to Discover Nuance',
                description: `Remove the most influential keywords from the graph to reveal the underlying ideas around them. This will help you extract the nuance and explore the peripheral topics that make a discourse special and can potentially serve as low-barrier entrance points.`,
                menuHint: `Graph → Select Nodes → click "Hide"`,
                showEvent: {
                    show: `revealUnderlyingIdeas`,
                    undo: `undoUnderlyingIdeas`,
                    closeOnShow: true,
                },
            },
            {
                label: 'Reiterate Steps 2 → 14',
                description: `Repeat steps 2 → 14: remove the keywords you studied to get to the less prominent keyword clusters that may still have sustantial search volume. Write down some insights you have while exploring the peripheral nodes and underlying ideas to make your research special and specific.`,
            },
            {
                label: 'Reset the Graph',
                description: `Once the possibilities are exhausted, return all the hidden nodes in the graph and reset the graph, to bring it to the original shape.`,
                menuHint: `right Graph Menu → undo Hide | Reset All`,
                showEvent: {
                    show: `hardResetGraph`,
                    undo: `unhardResetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label:
                    'Create Content and Ideas Based on Your Project Notes Insights',
                description: `Read through your project notes insights and use the AI to summarize them to get a better understanding of your discoveries. You can use the AI: Summary and AI: Outline buttons.`,
                menuHint: `Project Notes Insights → AI: Summarize `,
                showEvent: {
                    show: `showProjectNotesPanel`,
                    undo: `hideProjectNotesPanel`,
                },
            },
        ],
    },
    keywordResearch: {
        name: 'Keyword Research Workflow',
        steps: [
            {
                label: '1. Import Related Search Queries',
                description: `Get a list of suggested search queries for a certain topic to learn more about demand and understand what people are searching for.`,
            },
            {
                label: '2. Reveal the Top Keywords',
                description: `Locate the concepts that are relevant for you personally on the graph or find the top influential nodes and relations. Select them to see the keyword combinations and search queries where they appear. Target those for maximum impact.`,
                menuHint: `Graph Panel → Concepts`,
                showEvent: {
                    show: `selectTopNodes`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '3. Identify Top Keyword Combinations',
                description: `Locate the most prominent relations between the keywords in text, helping you understand the nuance and context better.`,
                menuHint: `Graph Panel → Concepts → Relations`,
                showEvent: {
                    show: `selectTopRelations`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '4. Save Your Ideas to Notes',
                description: `Save the top keywords and keyword combinations you found for your content / product strategy in the Project Notes. You can use it later to generate content and ideas with the help of AI.`,
                menuHint: `Project Notes → write → click Save`,
                showEvent: {
                    show: `showProjectNotesPanel`,
                    undo: `hideProjectNotesPanel`,
                },
            },
            {
                label: '5. Generate Ideas with AI',
                description: `Show the main topics to  understand the general search intent. Make sure your content relates to those. Use the AI to bridge the gaps between them with new ideas (Graph panel > Gaps).`,
                menuHint: `Graph Panel → Topics`,
                showEvent: {
                    show: `showAICategories`,
                    undo: `hideAICategories`,
                    closeOnShow: true,
                },
            },
            {
                label: '6. Locate Gatekeeper Keywords',
                description: `Some keywords have higher influence even if they are not frequent because they connect diverse topics. Create content and ads that target those to expand your reach.`,
                menuHint: `Graph Panel → Concepts → Conceptual Gateways`,
                showEvent: {
                    show: `highlightLatentNodesOnGraphAndAi`,
                    undo: `unHighlightNodesOnGraph`,
                    closeOnShow: true,
                },
            },
        ],
    },
    google: {
        name: 'Google Analysis Workflow',
        steps: [
            {
                label: '1. Import Google Search Results',
                description: `Type in a search query and get the Google search results to understand the context around. The words are the nodes, and the co-occurrences are the connections.`,
            },
            {
                label: '2. Get a Visual Summary of the Main Topics',
                description: `Reveal the main topical clusters in the search results as well as AI-generated summary and insights to get a better understanding of this discourse. The search keywords are hidden to emphasize the context around.`,
                menuHint: `Graph Panel → Topics → AI: Graph Summary`,
                showEvent: {
                    show: `showAICategories`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '3. Explore the Keywords and Topics',
                description: `Locate the concepts or topics that are relevant for you personally on the graph or find the top influential nodes and relations. Select them to see the keyword combinations and search results where they appear, to better understand the nuance.`,
                menuHint: `Graph Panel → Concepts`,
                showEvent: {
                    show: `selectTopNodes`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '4. Reveal the Blind Spots',
                description: `Some of the topics in the search results may not be well connected and produce structural gaps within. These gaps can be used to generate insight because they connect the existing ideas in new ways that are contextually relevant. Use the AI to generate a question to bridge this gap with a new idea.`,
                menuHint: `Graph Panel → Gaps`,
                showEvent: {
                    show: `highlightStructuralGapAndBridge`,
                    undo: `unHighlightStructuralGap`,
                    closeOnShow: true,
                },
            },
            {
                label: '5. Save Your Ideas to Notes',
                description: `Save the ideas you found interesting in the Project Notes. You can use it later to generate content and ideas with the help of AI.`,
                menuHint: `Project Notes → write → click Save`,
                showEvent: {
                    show: `showProjectNotesPanel`,
                    undo: `hideProjectNotesPanel`,
                },
            },

            {
                label: '6. Locate Gatekeeper Keywords',
                description: `Some concepts have higher influence even if they are not frequent because they connect diverse topics. Target those to better understand how to add your own ideas to this discourse.`,
                menuHint: `Graph Panel → Concepts → Conceptual Gateways`,
                showEvent: {
                    show: `highlightLatentNodesOnGraphAndAi`,
                    undo: `unHighlightNodesOnGraph`,
                    closeOnShow: true,
                },
            },
        ],
    },
    seo: {
        name: 'SEO Workflow',
        steps: [
            {
                label: '1. Import Search Results & Related Search Queries',
                description: `Import the search results for a certain topic (supply) and a list of the search queries that people use to search for it (search intent: demand).`,
            },
            {
                label: '2. Reveal Demand over Supply & Generate an AI Idea',
                description: `Switch to the graph statements view where you see the keyword relations people search for but do not find (Searched & Not Found). Use the AI in Analytics → Missing Content → Recommended Content Ideas to generate a business idea based on this niche.`,
                menuHint: `Statements → Filter → Searched & Not Found`,
                showEvent: {
                    show: `highlightMissing`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '3. Identify the Top Keywords',
                description: `Select the bigger keywords and see which keywords / search queries use them. Target those to address the underserved niche. If they are too obvious, hide them from the graph to reveal latent concepts.`,
                menuHint: `Graph Panel → Concepts`,
                showEvent: {
                    show: `selectTopNodes`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '4. Save Your Ideas to Notes',
                description: `Save the top keywords and keyword combinations you found for your content / product strategy in the Project Notes. You can use it later to generate content and ideas with the help of AI.`,
                menuHint: `Project Notes → write → click Save`,
                showEvent: {
                    show: `showProjectNotesPanel`,
                    undo: `hideProjectNotesPanel`,
                },
            },
            {
                label: '5. Identify Top Keyword Combinations',
                description: `Locate the most prominent relations between the keywords in text, helping you understand the nuance and context better. Target those for maximum impact.`,
                menuHint: `Graph panel  → Relations`,
                showEvent: {
                    show: `selectTopRelations`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '6. Generate Topical Clusters with AI',
                description: `They help understand the general search intent. Make sure your content relates to those.`,
                menuHint: `Analytics Panel → Main Topics  → Show High-Level Ideas`,
                showEvent: {
                    show: `showAICategories`,
                    undo: `hideAICategories`,
                    closeOnShow: true,
                },
            },
            {
                label: '7. Locate Gatekeeper Keywords',
                description: `Some keywords have higher influence even if they are not frequent because they connect diverse topics. Create content and ads that target those to expand your reach.`,
                menuHint: `Graph Panel → Concepts → Conceptual Gateways`,
                showEvent: {
                    show: `highlightLatentNodesOnGraphAndAi`,
                    undo: `unHighlightNodesOnGraph`,
                    closeOnShow: true,
                },
            },
        ],
    },
    niche: {
        name: 'Market Niche Workflow',
        steps: [
            {
                label: '1. Import the Current Demand & Supply',
                description: `Import the existing search results for a certain product or market niche (supply) and the keywords that people use to search for it (demand).`,
            },
            {
                label: '2. Reveal an Underserved Niche',
                description: `Switch to the graph statements view where you see the keyword relations people search for but do not find (Demand — Supply). Then generate a business idea based on this niche using the AI in Analytics → Niche Insights → Recommended Content Ideas.`,
                menuHint: `Statements → Filter → Demand — Supply`,
                showEvent: {
                    show: `highlightMissing`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '3. Identify the Top Keywords',
                description: `Select the bigger keywords and see which keywords / search queries use them. Target those to address the underserved niche. If they are too obvious, hide them from the graph to reveal latent concepts.`,
                menuHint: `Graph Panel → Concepts`,
                showEvent: {
                    show: `selectTopNodes`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '4. Save Your Ideas to Notes',
                description: `Save the top keywords and keyword combinations you found for your content / product strategy in the Project Notes. You can use it later to generate content and ideas with the help of AI.`,
                menuHint: `Project Notes → write → click Save`,
                showEvent: {
                    show: `showProjectNotesPanel`,
                    undo: `hideProjectNotesPanel`,
                },
            },
            {
                label: '5. Identify Top Keyword Combinations',
                description: `Locate the most prominent relations between the keywords in text, helping you understand the nuance and context better. Target those for maximum impact.`,
                menuHint: `Graph panel  → Relations`,
                showEvent: {
                    show: `selectTopRelations`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '6. Generate Topical Clusters with AI',
                description: `They help understand the general search intent. Make sure your content relates to those.`,
                menuHint: `Analytics Panel → Main Topics  → Show High-Level Ideas`,
                showEvent: {
                    show: `showAICategories`,
                    undo: `hideAICategories`,
                    closeOnShow: true,
                },
            },
            {
                label: '7. Locate Gatekeeper Keywords',
                description: `Some keywords have higher influence even if they are not frequent because they connect diverse topics. Create content and ads that target those to expand your reach.`,
                menuHint: `Graph Panel → Concepts → Conceptual Gateways`,
                showEvent: {
                    show: `highlightLatentNodesOnGraphAndAi`,
                    undo: `unHighlightNodesOnGraph`,
                    closeOnShow: true,
                },
            },
        ],
    },
    pdf: {
        name: 'Document Exploration Workflow',
        steps: [
            {
                label: '1. Add or Import a Document',
                description: `Upload PDF files, their content will be visualized as a network graph where the concepts (entities) are the nodes and their co-occurrences are the edges. `,
            },
            {
                label: '2. Get a Visual Summary of the Main Topics',
                description: `Reveal the main topical clusters in the text as well as AI-generated summary and insights to get a better understanding of the content.`,
                menuHint: `Graph Panel → Topics → AI: Graph Summary`,
                showEvent: {
                    show: `showAICategoriesAndAi`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '3. Reveal the Blind Spots',
                description: `Some of the topics inside the text may not be well connected and produce structural gaps within. These gaps can be used to generate insight because they connect the ideas in new ways that are contextually relevant. Use the AI to generate a question to bridge this gap with a new idea.`,
                menuHint: `Graph Panel → Gaps`,
                showEvent: {
                    show: `highlightStructuralGapAndBridge`,
                    undo: `unHighlightStructuralGap`,
                    closeOnShow: true,
                },
            },
            {
                label: '4. Explore Relations Between Ideas',
                description: `Find the nodes that seem relevant to you and click them to see the context where they appear. `,
                menuHint: `Analytics → Main Ideas or Relations`,
                showEvent: {
                    show: `selectTopRelations`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '5. Reveal Underlying Ideas to Discover Nuance',
                description: `Remove the most influential concepts from the graph to reveal the underlying ideas around them. This will help you extract the nuance and explore the peripheral topics that make a discourse special and can potentially serve as low-barrier entrance points.`,
                menuHint: `Graph → Select Nodes → click "Hide"`,
                showEvent: {
                    show: `revealUnderlyingIdeas`,
                    undo: `undoUnderlyingIdeas`,
                    closeOnShow: true,
                },
            },
            {
                label: '6. Save and Share Your Insights',
                description: `You can write down your ideas into project notes. You can also export the graph as an interactive image or as a high-resolution image.`,
                menuHint: `Project Notes Insights → Copy / Export `,
                showEvent: {
                    show: `showProjectNotesPanelAndShare`,
                    undo: `hideProjectNotesPanelAndShare`,
                },
            },
        ],
    },
    txt: {
        name: 'Document Exploration Workflow',
        steps: [
            {
                label: '1. Add or Import a Document',
                description: `Upload TXT/MD files, their content will be visualized as a network graph where the concepts (entities) are the nodes and their co-occurrences are the edges. `,
            },
            {
                label: '2. Get a Visual Summary of the Main Topics',
                description: `Reveal the main topical clusters in the text as well as AI-generated summary and insights to get a better understanding of the content.`,
                menuHint: `Graph Panel → Topics → AI: Graph Summary`,
                showEvent: {
                    show: `showAICategoriesAndAi`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '3. Reveal the Blind Spots',
                description: `Some of the topics inside the text may not be well connected and produce structural gaps within. These gaps can be used to generate insight because they connect the ideas in new ways that are contextually relevant. Use the AI to generate a question to bridge this gap with a new idea.`,
                menuHint: `Graph Panel → Gaps`,
                showEvent: {
                    show: `highlightStructuralGapAndBridge`,
                    undo: `unHighlightStructuralGap`,
                    closeOnShow: true,
                },
            },
            {
                label: '4. Explore Relations Between Ideas',
                description: `Find the nodes that seem relevant to you and click them to see the context where they appear. `,
                menuHint: `Analytics → Main Ideas or Relations`,
                showEvent: {
                    show: `selectTopRelations`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '5. Reveal Underlying Ideas to Discover Nuance',
                description: `Remove the most influential concepts from the graph to reveal the underlying ideas around them. This will help you extract the nuance and explore the peripheral topics that make a discourse special and can potentially serve as low-barrier entrance points.`,
                menuHint: `Graph → Select Nodes → click "Hide"`,
                showEvent: {
                    show: `revealUnderlyingIdeas`,
                    undo: `undoUnderlyingIdeas`,
                    closeOnShow: true,
                },
            },
            {
                label: '6. Save and Share Your Insights',
                description: `You can write down your ideas into project notes. You can also export the graph as an interactive image or as a high-resolution image.`,
                menuHint: `Project Notes Insights → Copy / Export `,
                showEvent: {
                    show: `showProjectNotesPanelAndShare`,
                    undo: `hideProjectNotesPanelAndShare`,
                },
            },
        ],
    },
    csv: {
        name: 'Spreadsheet Exploration Workflow',
        steps: [
            {
                label: '1. Add or Import a Document',
                description: `Upload CSV files, the columns you choose to analyze will be visualized as a network graph where the concepts (entities) are the nodes and their co-occurrences are the edges. The columns you choose as filters will be available for filtering the graph. `,
            },
            {
                label: '2. Get a Visual Summary of the Main Topics',
                description: `Reveal the main topical clusters in the text as well as AI-generated summary and insights to get a better understanding of the content.`,
                menuHint: `Graph Panel → Topics → AI: Graph Summary`,
                showEvent: {
                    show: `showAICategoriesAndAi`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '3. Filter the Graph by Categories or Sentiment',
                description: `Filter the graph by positive / negative sentiment to see which topics emerge depending on the tone of the text. Or if you specified columns to use as filters when importing data, use the filter panel above the statements.`,
                menuHint: `Analytics → Sentiment`,
                showEvent: {
                    show: `showFilter`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '4. Reveal the Blind Spots',
                description: `Some of the topics inside the text may not be well connected and produce structural gaps within. These gaps can be used to generate insight because they connect the ideas in new ways that are contextually relevant. Use the AI to generate a question to bridge this gap with a new idea.`,
                menuHint: `Graph Panel → Gaps`,
                showEvent: {
                    show: `highlightStructuralGapAndBridge`,
                    undo: `unHighlightStructuralGap`,
                    closeOnShow: true,
                },
            },

            {
                label: '5. Reveal Underlying Ideas to Discover Nuance',
                description: `Remove the most influential concepts from the graph to reveal the underlying ideas around them. This will help you extract the nuance and explore the peripheral topics that make a discourse special and can potentially serve as low-barrier entrance points.`,
                menuHint: `Graph → Select Nodes → click "Hide"`,
                showEvent: {
                    show: `revealUnderlyingIdeas`,
                    undo: `undoUnderlyingIdeas`,
                    closeOnShow: true,
                },
            },
            {
                label: '6. Save and Share Your Insights',
                description: `You can write down your ideas into project notes. You can also export the graph as an interactive image or as a high-resolution image.`,
                menuHint: `Project Notes Insights → Copy / Export `,
                showEvent: {
                    show: `showProjectNotesPanelAndShare`,
                    undo: `hideProjectNotesPanelAndShare`,
                },
            },
        ],
    },
    rss: {
        name: 'News Feed / RSS Exploration Workflow',
        steps: [
            {
                label: '1. Add or Import an RSS News Feed',
                description: `Select RSS feeds (or create your own) and visualize their content as a network graph where the concepts (entities) are the nodes and their co-occurrences are the edges. `,
            },
            {
                label: '2. Get a Visual Summary of the Main Topics',
                description: `Reveal the main topical clusters in the feeds analyzed as well as AI-generated summary and insights to get a better understanding of the content.`,
                menuHint: `Graph Panel → Topics → AI: Graph Summary`,
                showEvent: {
                    show: `showAICategoriesAndAi`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '3. Reveal the Blind Spots',
                description: `Some of the topics inside the text may not be well connected and produce structural gaps within. These gaps can be used to generate insight because they connect the ideas in new ways that are contextually relevant. Use the AI to generate a question to bridge this gap with a new idea.`,
                menuHint: `Graph Panel → Gaps`,
                showEvent: {
                    show: `highlightStructuralGapAndBridge`,
                    undo: `unHighlightStructuralGap`,
                    closeOnShow: true,
                },
            },
            {
                label: '4. Explore Relations Between Ideas',
                description: `Find the nodes that seem relevant to you and click them to see the context where they appear. `,
                menuHint: `Analytics → Main Ideas or Relations`,
                showEvent: {
                    show: `selectTopRelations`,
                    undo: `resetGraph`,
                    closeOnShow: true,
                },
            },
            {
                label: '5. Reveal Underlying Ideas to Discover Nuance',
                description: `Remove the most influential concepts from the graph to reveal the underlying ideas around them. This will help you extract the nuance and explore the peripheral topics that make a discourse special and can potentially serve as low-barrier entrance points.`,
                menuHint: `Graph → Select Nodes → click "Hide"`,
                showEvent: {
                    show: `revealUnderlyingIdeas`,
                    undo: `undoUnderlyingIdeas`,
                    closeOnShow: true,
                },
            },
            {
                label: '6. Save and Share Your Insights',
                description: `You can write down your ideas into project notes. You can also export the graph as an interactive image or as a high-resolution image.`,
                menuHint: `Project Notes Insights → Copy / Export `,
                showEvent: {
                    show: `showProjectNotesPanelAndShare`,
                    undo: `hideProjectNotesPanelAndShare`,
                },
            },
        ],
    },
}

/** InfraNodus vs KeywordGraph wording for the apps onboarding flow (791ef5b). */
export function getStepperFlows() {
    if (isKeywordGraphApp()) return stepperFlows
    const apps = stepperFlows.apps
    return {
        ...stepperFlows,
        apps: {
            ...apps,
            name: 'Learn to Use InfraNodus',
            steps: apps.steps.map((step, i) =>
                i === 3
                    ? {
                          ...step,
                          label: '4. Brainstorm with a knowledge graph',
                      }
                    : step
            ),
        },
    }
}

export default stepperFlows
